import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import { SelectProps } from "@mantine/core";
import { getRuian, Ruian } from "@/requests/ruian";
import { Address } from "@/types/address";
import { useContext, useEffect, useState } from "react";
import { joinStringsByDelimiter } from "@/utils";
import { EnumerationsContext } from "@/contexts/EnumerationsContext";
import Select from "@/components/Select";

type AddressAutosuggestProps = Omit<SelectProps, "value" | "onChange"> & {
  value?: Address;
  onChange?: (address: Address) => void;
};

const AddressAutosuggest = ({ value, onChange, ...rest }: AddressAutosuggestProps) => {
  const { czechCountryId } = useContext(EnumerationsContext);
  const [searchString, setSearchString] = useState(createAddressString(value));
  const [debouncedSearch] = useDebouncedValue(searchString, 200);

  const { data } = useQuery({
    queryKey: ["addresses/search", debouncedSearch],
    queryFn: async () => {
      const addresses1 = await getRuian({ query: transformSearchQuery(debouncedSearch) });
      return addresses1.slice(0, 10);
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: debouncedSearch.length > 1,
  });

  useEffect(() => setSearchString(createAddressString(value)), [value]);

  const ruianData = data ?? [];
  const options = ruianData.map(address => ({
    value: `${address.ruianId}`,
    label: createAddressString(createAddressFromRuian(address, czechCountryId)),
  }));

  const currentValueIncluded = options.find(o => o.value === String(value?.addressPlaceCode));
  if (value?.addressPlaceCode && !currentValueIncluded) {
    options.push({
      value: String(value.addressPlaceCode),
      label: createAddressString(value),
    });
  }

  return (
    <Select
      searchable
      searchValue={searchString}
      onSearchChange={setSearchString}
      data={options}
      defaultValue={value?.addressPlaceCode?.toString() ?? null}
      onOptionSubmit={optionValue => {
        const address = ruianData.find(a => `${a.ruianId}` === optionValue);
        if (address) {
          onChange?.(createAddressFromRuian(address, czechCountryId));
        }
      }}
      filter={({ options }) =>
        currentValueIncluded ? options : options.filter(o => !("value" in o) || o.value !== String(value?.addressPlaceCode))
      }
      allowDeselect={false}
      withRightSection={false}
      pii
      {...rest}
    />
  );
};

export default AddressAutosuggest;

const transformSearchQuery = (query: string) => query.replace(/\bev\.?\s*(\d+)/gi, "E$1");

const createAddressString = (address?: Address) => {
  let result = "";
  if (!address) {
    return result;
  }
  if (address.street) {
    result = address.street;
  }
  if (address.houseNumber) {
    result = joinStringsByDelimiter(result, address.houseNumber, "");
    if (address.streetNumber) {
      result += `/${address.streetNumber}`;
    }
  }
  if (address.evidenceNumber) {
    result += ` ev. ${address.evidenceNumber}`;
  }
  if (address.city) {
    result = joinStringsByDelimiter(result, address.city);
  }
  if (address.zip) {
    result = joinStringsByDelimiter(result, address.zip);
  }
  return result;
};

const createAddressFromRuian = (ruianAddress: Ruian, czechCountryId: string) => {
  return {
    addressPlaceCode: ruianAddress.ruianId,
    city: ruianAddress.city,
    cityPart: ruianAddress.cityPart,
    countryCode: czechCountryId,
    houseNumber: ruianAddress.houseNumber ?? "",
    street: ruianAddress.street,
    streetNumber: ruianAddress.streetNumber ? `${ruianAddress.streetNumber}` : "",
    zip: ruianAddress.zip,
    cadastralTerritory: "",
    cadastralTerritoryCode: null,
    evidenceNumber: ruianAddress.registrationNumber,
    nameCareOf: null,
  };
};
